import React, { useState, useEffect, useRef } from "react";
import {
  Phone,
  FileText,
  Star,
  Megaphone,
  Truck,
  Settings,
  ShieldCheck,
  FileDown,
  Bell,
  BellOff,
  LogOut,
  Loader2,
  Download,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Check,
  Store,
  Globe,
  Users,
  Target,
  MessageSquare,
} from "lucide-react";
import { supabase } from "../supabaseClient";
// Stores whose website is the sales assistant: the Website tab opens it with
// this dashboard's session, so the store signs in there the way it signed
// in here (Phillip, 2026-10-08).
import STORE_WEBSITES from "../config/storeWebsites.json";

// Mirrors DEFAULT_ADMIN_EMAILS in api/phone-agents-2/store-brands.py.
const PLATFORM_ADMIN_EMAILS = ["byars.jp5@gmail.com"];
import {
  decryptStorePassword,
  STORED_MULTI_STORE_CREDENTIALS_KEY,
} from "../utils/storeCredentialsCrypto";
/**
 * Sidebar - Enterprise-grade left navigation
 *
 * Features:
 *  - Collapsible (248px ↔ 72px) with localStorage persistence
 *  - Active state with accent bar + blue tint
 *  - Per-item colored badges (counts, alerts)
 *  - Tooltips appear when collapsed, on hover, anchored to the right of the icon
 *  - Custom store switcher (button + popover, supports many stores)
 *  - Bottom utility cluster: push notifications, sales tax export, settings,
 *    PWA install, sign out — all collapse-aware
 *  - Honors `tabVisibility` and `visibleTabCount` for parity with old nav
 *
 * Drop into ReceiptDashboardDesktop.jsx in place of the fixed top nav.
 * Adjusts content width via the `pl-[72px]` / `pl-[248px]` wrapper margin.
 */

const SIDEBAR_W_EXPANDED = 248; // px
const SIDEBAR_W_COLLAPSED = 72; // px
const STORAGE_KEY = "exitium.sidebar.collapsed";
const STORED_STORES_KEY = "exitium.storedStores";

const Sidebar = ({
  // Active view
  activeView,
  onNavigate, // (view: 'phoneAgent' | 'orders' | 'reviews' | 'campaigns') => void

  // Visibility / disable rules (same shape as before)
  tabVisibility = {},
  visibleTabCount = 3,

  // Stores
  stores = [],
  currentStore,
  onStoreChange, // (store) => void

  // Badges
  ordersBadgeCount = 0,

  // Bottom-area actions
  onOpenSettings,
  onOpenSalesTaxExport,
  pushSupported = false,
  pushEnabled = false,
  pushLoading = false,
  onTogglePush,
  showInstallPrompt = false,
  isInstalled = false,
  onInstallPWA,
  onLogout,

  // Collapse control (lifted to parent so it can shift content margin)
  collapsed,
  onToggleCollapsed,
}) => {
  // Exitium admin sees a link to /admin/brands. Only decides whether the
  // link shows; the page's API is the real gate (by verified login email).
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  useEffect(() => {
    const check = (email) => setIsPlatformAdmin(PLATFORM_ADMIN_EMAILS.includes((email || "").toLowerCase()));
    supabase.auth.getUser().then(({ data }) => check(data?.user?.email));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => check(session?.user?.email));
    return () => sub?.subscription?.unsubscribe?.();
  }, []);
  const width = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;

  return (
    <aside
      className="hidden lg:flex fixed top-0 left-0 h-screen flex-col bg-white border-r-2 border-slate-300 shadow-sm z-50 transition-[width] duration-200 ease-out"
      style={{ width }}
      aria-label="Primary navigation"
    >
      {/* Brand */}
      <div
        className={`h-16 flex items-center border-b border-slate-200 ${
          collapsed ? "justify-center px-2" : "px-4 gap-3"
        }`}
      >
        <div className="w-10 h-10 rounded-lg overflow-hidden shadow-md flex-shrink-0 ring-2 ring-blue-100">
          <img src="/e.png" alt="Exitium" className="w-full h-full object-cover" />
        </div>
        {!collapsed && (
          <span className="text-xl font-bold text-slate-900 tracking-tight">
            Exitium
          </span>
        )}
      </div>

      {/* Store switcher */}
      <div className={`${collapsed ? "px-2 py-3" : "px-3 py-3"} border-b border-slate-200`}>
        <StoreSwitcher
          collapsed={collapsed}
          stores={stores}
          currentStore={currentStore}
          onStoreChange={onStoreChange}
        />
      </div>

      {/* Primary navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {!collapsed && (
          <div className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Workspace
          </div>
        )}
        <ul className={`space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
          {tabVisibility.phoneAgent !== false && (
            <NavItem
              icon={Phone}
              label="Phone Agent"
              tip="Your AI phone line: call log, follow-ups, transfers, voice and catalog"
              active={activeView === "phoneAgent"}
              disabled={visibleTabCount === 1 && activeView === "phoneAgent"}
              onClick={() => onNavigate("phoneAgent")}
              collapsed={collapsed}
            />
          )}
          {tabVisibility.orders && (
            <NavItem
              icon={FileText}
              label="Orders"
              tip="Receipts: item status, payments, delivery and customer texts"
              active={activeView === "orders"}
              disabled={visibleTabCount === 1 && activeView === "orders"}
              onClick={() => onNavigate("orders")}
              collapsed={collapsed}
              badge={
                ordersBadgeCount > 0
                  ? { value: ordersBadgeCount, tone: "blue" }
                  : null
              }
            />
          )}
          {/* Chat — one inbox per person. Testing Store only while it is
              checked (chatAccess.js). Strict === true. */}
          {tabVisibility.chat === true && (
            <NavItem
              icon={MessageSquare}
              label="Chat"
              tip="Every customer's texts in one place, with their receipts beside them"
              active={activeView === "chat"}
              onClick={() => onNavigate("chat")}
              collapsed={collapsed}
            />
          )}
          {/* Deliveries — Testing Store only. Strict === true: the hook's
              per-store allowlist (deliveriesAccess.js) is the only thing that
              sets deliveries true; live stores never see this item. */}
          {tabVisibility.deliveries === true && (
            <NavItem
              icon={Truck}
              label="Deliveries"
              tip="Plan routes and dispatch drivers"
              active={activeView === "deliveries"}
              disabled={visibleTabCount === 1 && activeView === "deliveries"}
              onClick={() => onNavigate("deliveries")}
              collapsed={collapsed}
            />
          )}
          {tabVisibility.reviews !== false && (
            <NavItem
              icon={Star}
              label="Reviews"
              tip="Customer ratings and Google review requests"
              active={activeView === "reviews"}
              disabled={visibleTabCount === 1 && activeView === "reviews"}
              onClick={() => onNavigate("reviews")}
              collapsed={collapsed}
            />
          )}
          {/* Marketing Studio — owner/testing accounts only. Strict === true:
              the hook's MARKETING_OWNER_EMAILS allowlist is the only thing
              that sets campaigns true; live stores stay pinned false, so
              this item never renders for them. */}
          {tabVisibility.campaigns === true && (
            <NavItem
              icon={Megaphone}
              label="Marketing"
              tip="Flyers, deals and campaigns the phone agent can text out"
              active={activeView === "campaigns"}
              disabled={visibleTabCount === 1 && activeView === "campaigns"}
              onClick={() => onNavigate("campaigns")}
              collapsed={collapsed}
            />
          )}
          <NavItem
            icon={Users}
            label="Contacts"
            tip="Your text list: who opted in to deals by text, leads from the floor, who opted out"
            active={activeView === "contacts"}
            onClick={() => onNavigate("contacts")}
            collapsed={collapsed}
          />
          {/* Sales — Exitium's own pipeline for selling to furniture stores.
              Strict === true: tabVisibility.sales is set only by the
              per-store + per-user gate in salesPipelineAccess.js (Testing
              Store on the owner login), so this never renders for a live
              store. */}
          {tabVisibility.sales === true && (
            <NavItem
              icon={Target}
              label="Sales"
              tip="Exitium's pipeline: the stores we are selling to, who owns each one, what is due today"
              active={activeView === "sales"}
              onClick={() => onNavigate("sales")}
              collapsed={collapsed}
            />
          )}
          {STORE_WEBSITES[currentStore?.id]?.url && (
            <NavItem
              icon={Globe}
              label={STORE_WEBSITES[currentStore.id].label || "Website"}
              tip={STORE_WEBSITES[currentStore.id].tip || "Open the store's website as the sales assistant"}
              active={false}
              onClick={() => openStoreWebsite(STORE_WEBSITES[currentStore.id].url)}
              collapsed={collapsed}
            />
          )}
        </ul>
      </nav>

      {/* Utility cluster */}
      <div className="border-t border-slate-200 py-3">
        {!collapsed && (
          <div className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Account
          </div>
        )}
        <ul className={`space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
          {pushSupported && (
            <UtilityItem
              icon={pushLoading ? Loader2 : pushEnabled ? Bell : BellOff}
              iconClassName={pushLoading ? "animate-spin" : ""}
              label={pushEnabled ? "Notifications on" : "Notifications off"}
              tip={
                pushEnabled
                  ? "Turn off push alerts for new texts and escalations on this device"
                  : "Get push alerts on this device when a customer texts or needs a person"
              }
              tone={pushEnabled ? "green" : "default"}
              onClick={onTogglePush}
              disabled={pushLoading}
              collapsed={collapsed}
            />
          )}
          <UtilityItem
            icon={FileDown}
            label="Export sales & tax"
            tip="Download a CSV of receipts with subtotal and tax for a period"
            onClick={onOpenSalesTaxExport}
            collapsed={collapsed}
          />
          {isPlatformAdmin && (
            <UtilityItem
              icon={ShieldCheck}
              label="Brands by store"
              tip="Exitium admin: switch manufacturer catalogs on or off for every store"
              onClick={() => { window.location.href = "/admin/brands"; }}
              collapsed={collapsed}
            />
          )}
          <UtilityItem
            icon={Settings}
            label="Store settings"
            tip="Store addresses, sales tax rate, tagline and Google review link"
            onClick={onOpenSettings}
            collapsed={collapsed}
          />
          {showInstallPrompt && !isInstalled && (
            <UtilityItem
              icon={Download}
              label="Install app"
              tip="Add the dashboard to this device so it opens like an app"
              tone="blue-solid"
              onClick={onInstallPWA}
              collapsed={collapsed}
            />
          )}
          <UtilityItem
            icon={LogOut}
            label="Sign out"
            tip="Log out of this store on this device"
            tone="danger"
            onClick={onLogout}
            collapsed={collapsed}
          />
        </ul>
      </div>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="border-t border-slate-200 h-10 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        data-tip={
          collapsed
            ? "Expand the sidebar to show labels"
            : "Collapse the sidebar to icons only"
        }
      >
        {collapsed ? (
          <ChevronsRight className="w-4 h-4" />
        ) : (
          <ChevronsLeft className="w-4 h-4" />
        )}
      </button>
    </aside>
  );
};

/* ---------- Subcomponents ---------- */

// Hover tips come from the app-wide TooltipLayer (data-tip). Expanded rows
// show the "what this is" tip; collapsed rows prefix it with the label since
// the label itself is hidden. The old inline group-hover tooltip is gone so
// the two never stack.
const navTip = (label, tip, collapsed) =>
  collapsed ? (tip ? `${label} — ${tip}` : label) : tip || undefined;

// The store's website, signed in: the site's /sales page reads this
// dashboard's session from the URL fragment (never sent to any server) and
// trades it for its own sales session. Without a session it still opens,
// and the site asks for the Exitium email and password.
async function openStoreWebsite(url) {
  let token = "";
  try {
    const { data } = await supabase.auth.getSession();
    token = data?.session?.access_token || "";
  } catch {
    token = "";
  }
  const target = `${url.replace(/\/+$/, "")}/sales${token ? `#exitium=${encodeURIComponent(token)}` : ""}`;
  window.open(target, "_blank", "noopener");
}

const NavItem = ({
  icon: Icon,
  label,
  tip,
  active,
  disabled,
  onClick,
  collapsed,
  badge,
}) => {
  const base =
    "group relative w-full flex items-center rounded-lg font-medium text-sm transition-all";
  const padding = collapsed ? "h-11 justify-center" : "h-11 px-3 gap-3";
  const stateClasses = active
    ? "bg-blue-50 text-blue-700"
    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100";

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-current={active ? "page" : undefined}
        aria-label={collapsed ? label : undefined}
        data-tip={navTip(label, tip, collapsed)}
        className={`${base} ${padding} ${stateClasses} disabled:cursor-default disabled:opacity-100`}
      >
        {/* Active accent bar */}
        {active && (
          <span
            aria-hidden
            className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-blue-600"
          />
        )}

        {/* Icon w/ optional badge dot when collapsed */}
        <span className="relative flex-shrink-0">
          <Icon className="w-5 h-5" />
          {collapsed && badge && (
            <span
              className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${badgeToneClasses(
                badge.tone,
                true,
              )}`}
            >
              {badge.value > 99 ? "99+" : badge.value}
            </span>
          )}
        </span>

        {!collapsed && <span className="flex-1 text-left">{label}</span>}

        {!collapsed && badge && (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeToneClasses(
              badge.tone,
              active,
            )}`}
          >
            {badge.value}
          </span>
        )}

      </button>
    </li>
  );
};

const UtilityItem = ({
  icon: Icon,
  iconClassName = "",
  label,
  tip,
  tone = "default",
  onClick,
  disabled,
  collapsed,
}) => {
  const toneClasses =
    tone === "danger"
      ? "text-slate-600 hover:text-red-600 hover:bg-red-50"
      : tone === "green"
        ? "text-green-700 hover:bg-green-50"
        : tone === "blue-solid"
          ? "text-white bg-blue-600 hover:bg-blue-700"
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100";

  const padding = collapsed ? "h-10 justify-center" : "h-10 px-3 gap-3";

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={collapsed ? label : undefined}
        data-tip={navTip(label, tip, collapsed)}
        className={`group relative w-full flex items-center rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${padding} ${toneClasses}`}
      >
        <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${iconClassName}`} />
        {!collapsed && <span className="flex-1 text-left">{label}</span>}
      </button>
    </li>
  );
};

const StoreSwitcher = ({ collapsed, stores, currentStore, onStoreChange }) => {
  const [open, setOpen] = useState(false);
  const [storedStores, setStoredStores] = useState([]);
  const [storedCredentials, setStoredCredentials] = useState([]);
  const [switchError, setSwitchError] = useState("");
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const ref = useRef(null);

  const loadStoredCredentials = () => {
    try {
      const rawCreds = window.localStorage.getItem(STORED_MULTI_STORE_CREDENTIALS_KEY);
      const parsedCreds = rawCreds ? JSON.parse(rawCreds) : [];
      setStoredCredentials(Array.isArray(parsedCreds) ? parsedCreds : []);
    } catch {
      setStoredCredentials([]);
    }
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORED_STORES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      setStoredStores(Array.isArray(parsed) ? parsed : []);
    } catch {
      setStoredStores([]);
    }
    loadStoredCredentials();
  }, []);

  useEffect(() => {
    const syncUser = async () => {
      const { data } = await supabase.auth.getUser();
      setCurrentUserEmail(data?.user?.email || "");
    };
    syncUser();
    const { data: authSub } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUserEmail(session?.user?.email || "");
    });
    return () => authSub?.subscription?.unsubscribe?.();
  }, []);

  useEffect(() => {
    loadStoredCredentials();
  }, [currentUserEmail]);

  useEffect(() => {
    if (!Array.isArray(stores) || stores.length === 0) return;
    try {
      const lightweightStores = stores.map((s) => ({ id: s.id, name: s.name }));
      window.localStorage.setItem(
        STORED_STORES_KEY,
        JSON.stringify(lightweightStores),
      );
      setStoredStores(lightweightStores);
    } catch {
      /* ignore storage failures */
    }
  }, [stores]);

  const switcherStores = storedStores.length > 0 ? storedStores : stores;
  const hasMultiple =
    storedCredentials.length > 1 || switcherStores.length > 1;

  const forceFullAppRefresh = () => {
    // Force a full app reload to clear any stale in-memory store/account data.
    window.location.reload();
  };

  const handleStorePick = (pickedStore) => {
    const liveStore = stores.find((s) => s.id === pickedStore?.id) || pickedStore;
    onStoreChange(liveStore);
    setOpen(false);
    forceFullAppRefresh();
  };

  const handleCredentialPick = async (credential) => {
    setSwitchError("");
    const pin = window.prompt("Enter your switching PIN");
    if (!pin) return;

    setIsSwitchingAccount(true);
    try {
      const password = await decryptStorePassword(
        credential.encryptedPassword,
        credential.iv,
        pin,
      );
      const { error } = await supabase.auth.signInWithPassword({
        email: credential.email,
        password,
      });
      if (error) throw error;
      loadStoredCredentials();
      setOpen(false);
      forceFullAppRefresh();
    } catch (error) {
      setSwitchError(error?.message || "Failed to switch store account.");
    } finally {
      setIsSwitchingAccount(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Collapsed: just a small icon button (with tooltip)
  if (collapsed) {
    return (
      <div className="relative group flex justify-center" ref={ref}>
        <button
          type="button"
          onClick={() => hasMultiple && setOpen((o) => !o)}
          className="w-11 h-11 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
          aria-label={`Store: ${currentStore?.name || "none"}`}
          data-tip={
            hasMultiple
              ? `${currentStore?.name || "No store"} — click to switch store`
              : `${currentStore?.name || "No store"} (your only store)`
          }
        >
          <Store className="w-[18px] h-[18px]" />
        </button>

        {/* Popover for collapsed mode */}
        {open && hasMultiple && (
          <div className="absolute left-full top-0 ml-3 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
            {storedCredentials.length > 0 ? (
              <CredentialList
                credentials={storedCredentials}
                currentEmail={currentUserEmail}
                onPick={handleCredentialPick}
                isSwitching={isSwitchingAccount}
              />
            ) : (
              <StoreList
                stores={switcherStores}
                currentStore={currentStore}
                onPick={handleStorePick}
              />
            )}
            {switchError && (
              <div className="px-3 py-2 text-xs text-red-600 border-t border-slate-200">
                {switchError}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Expanded
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => hasMultiple && setOpen((o) => !o)}
        data-tip={
          hasMultiple
            ? "Switch to another store you manage"
            : "The store you're signed into (no other stores on this login)"
        }
        className={`w-full flex items-center gap-2.5 px-3 h-11 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-colors ${
          !hasMultiple ? "cursor-default" : "cursor-pointer"
        }`}
      >
        <span className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
          <Store className="w-4 h-4 text-blue-600" />
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
            Store
          </span>
          <span className="block text-sm font-semibold text-slate-900 truncate leading-tight mt-0.5">
            {currentStore?.name || "—"}
          </span>
        </span>
        {hasMultiple && (
          <ChevronDown
            className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {open && hasMultiple && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 max-h-72 overflow-y-auto">
          {storedCredentials.length > 0 ? (
            <CredentialList
              credentials={storedCredentials}
              currentEmail={currentUserEmail}
              onPick={handleCredentialPick}
              isSwitching={isSwitchingAccount}
            />
          ) : (
            <StoreList
              stores={switcherStores}
              currentStore={currentStore}
              onPick={handleStorePick}
            />
          )}
          {switchError && (
            <div className="px-3 py-2 text-xs text-red-600 border-t border-slate-200">
              {switchError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StoreList = ({ stores, currentStore, onPick }) => (
  <ul>
    {stores.map((s) => {
      const isCurrent = s.id === currentStore?.id;
      return (
        <li key={s.id}>
          <button
            type="button"
            onClick={() => onPick(s)}
            data-tip={isCurrent ? "This is the store you're in now" : "Switch to this store (the page reloads)"}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
              isCurrent
                ? "bg-blue-50 text-blue-700 font-semibold"
                : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="flex-1 truncate">{s.name}</span>
            {isCurrent && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
          </button>
        </li>
      );
    })}
  </ul>
);

const CredentialList = ({ credentials, currentEmail, onPick, isSwitching }) => (
  <ul>
    {credentials.map((c) => {
      const isCurrent = c.email === currentEmail;
      return (
        <li key={c.email}>
          <button
            type="button"
            onClick={() => onPick(c)}
            disabled={isSwitching}
            data-tip={isCurrent ? "This is the account you're signed into now" : "Sign into this store account (asks for your switching PIN)"}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
              isCurrent
                ? "bg-blue-50 text-blue-700 font-semibold"
                : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="flex-1 truncate">{c.email}</span>
            {isCurrent && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
          </button>
        </li>
      );
    })}
  </ul>
);

/* ---------- helpers ---------- */

function badgeToneClasses(tone, emphasized) {
  // `emphasized` = active row (or collapsed dot) — slightly stronger
  switch (tone) {
    case "blue":
      return emphasized
        ? "bg-blue-100 text-blue-700"
        : "bg-slate-200 text-slate-600";
    case "emerald":
      return emphasized
        ? "bg-emerald-100 text-emerald-700"
        : "bg-emerald-100 text-emerald-600";
    case "red":
      return "bg-red-100 text-red-700";
    default:
      return "bg-slate-200 text-slate-600";
  }
}

/* ---------- Hook for consumers ---------- */

/**
 * Optional convenience hook — encapsulates collapsed state + localStorage.
 * Usage:
 *   const { collapsed, toggle } = useSidebarCollapsed();
 */
export const useSidebarCollapsed = () => {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore quota / privacy errors */
    }
  }, [collapsed]);

  return {
    collapsed,
    toggle: () => setCollapsed((c) => !c),
    setCollapsed,
    width: collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED,
  };
};

export { SIDEBAR_W_EXPANDED, SIDEBAR_W_COLLAPSED };
export default Sidebar;